import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function UploadCV({
  onFileSelected,
  setUpdateDocument,
  documents,
  expertData,
}) {
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const contentRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(0);

  const triggerFileSelect = () => fileInputRef.current.click();

  const getFileIcon = (ext) => {
    switch (ext.toLowerCase()) {
      case "pdf": return "fa-file-pdf text-red-500";
      case "doc":
      case "docx": return "fa-file-word text-blue-500";
      case "xls":
      case "xlsx": return "fa-file-excel text-green-500";
      default: return "fa-file text-gray-400";
    }
  };

  const getFormattedTime = () => {
    const d = new Date();
    const pad = (n) => n.toString().padStart(2, "0");
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}'${pad(d.getMinutes())}`;
  };

  const generateUniqueFilenames = (files) => {
    const updated = [...files];
    const nameMap = {};

    for (let i = 0; i < updated.length; i++) {
      const { prefix, timestamp } = updated[i];
      const base = `${prefix}-${expertData.name}-${timestamp}`;
      nameMap[base] = (nameMap[base] || 0) + 1;
      const suffix = nameMap[base] > 1 ? `_${nameMap[base] - 1}` : "";
      updated[i].documentLinks[0].filename = `${base}${suffix}`;
      updated[i].documentLinks[0].isDuplicate = nameMap[base] > 1;
    }

    return updated;
  };

  const readFileAsync = (file, prefix, timestamp) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      const ext = file.name.split(".").pop();

      reader.onload = () => {
        resolve({
          prefix,
          timestamp,
          documentLinks: [
            {
              fileFormat: ext.toLowerCase(),
              fileLoad: reader.result.split(",")[1],
              uploadedAt: timestamp,
              filename: "", // will be set later
              isDuplicate: false,
            },
          ],
        });
      };

      reader.readAsDataURL(file);
    });
  };

  const handleFiles = async (incomingFiles) => {
    const timestamp = getFormattedTime();
    const prefix = "CV";
    const fileObjs = await Promise.all(
      incomingFiles.map((file) => readFileAsync(file, prefix, timestamp))
    );

    const combined = [...files, ...fileObjs];
    const renamed = generateUniqueFilenames(combined);
    setFiles(renamed);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length) handleFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    const inputFiles = Array.from(e.target.files);
    if (inputFiles.length) handleFiles(inputFiles);
  };

  const handleFileConfirmation = (e) => {
    e.preventDefault();
    onFileSelected(files);
  };

  const handlePrefixChange = (e, index) => {
    const newFiles = [...files];
    newFiles[index].prefix = e.target.value;
    const updated = generateUniqueFilenames(newFiles);
    setFiles(updated);
  };

  const handleDelete = (index) => {
    const filtered = files.filter((_, i) => i !== index);
    const updated = generateUniqueFilenames(filtered);
    setFiles(updated);
  };

  useEffect(() => {
    if (contentRef.current) {
      const resize = () => {
        if (!contentRef.current) return; // ✅ add this guard
        const baseHeight = contentRef.current.scrollHeight;
        const extraPadding = 48; // For bottom spacing (e.g., Upload button)
        setContainerHeight(baseHeight + (files.length > 0 ? extraPadding : 0));
      };
  
      resize();
  
      const observer = new ResizeObserver(resize);
      observer.observe(contentRef.current);
  
      return () => {
        observer.disconnect();
      };
    }
  }, [files]);
  
  return (
    <>
      {/* Close Button */}
      <button
        className="absolute top-3 right-4 text-gray-400 hover:text-gray-600 text-xl"
        onClick={() => setUpdateDocument(false)}
      >
        &times;
      </button>

      <h3 className="text-xl font-semibold text-center mb-6">Upload CV File</h3>

      {/* Drop Zone */}
      <div
        onDragEnter={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragging(false);
        }}
        onDrop={handleFileDrop}
        className={`border-dashed border-2 ${
          isDragging ? "border-blue-400 bg-blue-50" : "border-gray-300"
        } rounded-lg w-full p-8 text-center text-gray-500 hover:bg-gray-50 transition`}
      >
        <p className="mb-2">Drag & Drop CV files here</p>
        <p className="text-sm">
          or{" "}
          <button
            type="button"
            className="text-blue-600 font-medium underline hover:text-blue-800"
            onClick={triggerFileSelect}
          >
            Select File
          </button>
        </p>
        <input
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.xls,.xlsx"
          className="hidden"
          ref={fileInputRef}
          onChange={handleFileInput}
        />
      </div>

      {/* Files List */}
      <motion.div
        style={{ overflow: "hidden" }}
        animate={{ height: containerHeight }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        <div ref={contentRef} className="space-y-4 mt-6">
          {files.map((item, index) => {
            const doc = item.documentLinks[0];
            const ext = doc.fileFormat;
            const timestampMatch = doc.filename.match(/(\d{2}-\d{2}-\d{4} \d{2}'\d{2})(_\d+)?$/);
            const displayTimestamp = timestampMatch?.[1] || "";
            const suffix = timestampMatch?.[2] || "";

            return (
              <motion.div
                key={`${doc.filename}-${index}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex flex-wrap md:flex-nowrap items-center gap-3 border rounded p-3 relative bg-gray-50"
              >
                <i className={`fa-solid ${getFileIcon(ext)} text-2xl`} />

                <select
                  value={item.prefix}
                  onChange={(e) => handlePrefixChange(e, index)}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value="CV">CV</option>
                  <option value="PL">PL</option>
                  <option value="Other">Other</option>
                </select>

                <span className="text-sm text-gray-700 font-mono truncate max-w-[500px]">
                  {`- ${expertData.name} - ${displayTimestamp}${suffix}`}
                </span>

                <span className="text-sm text-gray-500">.{ext}</span>

                <i
                  className="fa fa-trash text-red-500 hover:text-red-700 absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
                  onClick={() => handleDelete(index)}
                />
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Upload Button */}
      <div className="text-center mt-6">
        <button
          disabled={files.length === 0}
          className="bg-emerald-600 text-white px-6 py-2 rounded-lg text-lg hover:bg-emerald-700 disabled:opacity-50"
          onClick={handleFileConfirmation}
        >
          Upload
        </button>
      </div>
    </>
  );
}